import type { SyntheticDataBatch } from "services/SyntheticDataService";

/** True until the worker saves a final status, so the batch keeps getting live updates. */
export function isGenerating(batch: SyntheticDataBatch) {
    return batch.status === "Queued" || batch.status === "Running";
}

export function hasGeneratingBatches(batches: SyntheticDataBatch[]) {
    return batches.some(isGenerating);
}

/** Only succeeded batches save calls; a failed batch saves none. */
export function isSucceeded(batch: SyntheticDataBatch) {
    return batch.status === "Succeeded";
}

/** Whole-number percent of the requested calls written so far, capped at 100. */
export function progressPercent(
    generatedCount: number,
    requestedCount: number,
) {
    if (requestedCount <= 0) return 0;
    return Math.min(100, Math.floor((generatedCount / requestedCount) * 100));
}

/** Calls written and requested across all of the model's batches. */
export function getBatchTotals(batches: SyntheticDataBatch[]) {
    return batches.reduce(
        (totals, batch) => ({
            generatedCount: totals.generatedCount + batch.generatedCount,
            requestedCount: totals.requestedCount + batch.requestedCount,
        }),
        { generatedCount: 0, requestedCount: 0 },
    );
}

/** Synthetic calls saved for each result, in the order results first appear. */
export function getSavedCallsByResult(batches: SyntheticDataBatch[]) {
    const callsByResult = new Map<string, number>();
    for (const batch of batches) {
        if (!isSucceeded(batch)) continue;
        callsByResult.set(
            batch.labelValue,
            (callsByResult.get(batch.labelValue) ?? 0) + batch.generatedCount,
        );
    }
    return Array.from(callsByResult, ([result, syntheticCalls]) => ({
        result,
        syntheticCalls,
    }));
}

export function getSavedCallTotal(batches: SyntheticDataBatch[]) {
    return getBatchTotals(batches.filter(isSucceeded)).generatedCount;
}

/** When the most recent succeeded batch finished. */
export function getLastCompletedOn(batches: SyntheticDataBatch[]) {
    let latest: string | undefined;
    for (const batch of batches) {
        if (!isSucceeded(batch) || !batch.completedOn) continue;
        if (!latest || Date.parse(batch.completedOn) > Date.parse(latest))
            latest = batch.completedOn;
    }
    return latest;
}

/**
 * Headline for a set where any result failed, which counts the whole set as failed.
 * Undefined when nothing failed.
 */
export function getFailureHeadline(batches: SyntheticDataBatch[]) {
    if (!batches.some((batch) => batch.status === "Failed")) return undefined;
    const totals = getBatchTotals(batches);
    return `Generation stopped at ${totals.generatedCount.toLocaleString()} of ${totals.requestedCount.toLocaleString()} ${
        totals.requestedCount === 1 ? "call" : "calls"
    }`;
}

const setupMinutes = 5;
const secondsPerCall = 15;

/** Rough time left: 15 seconds for each call not yet written, plus 5 minutes of setup until the first call. */
export function getTimeLeftText(
    generatedCount: number,
    requestedCount: number,
) {
    const callsLeft = Math.max(0, requestedCount - generatedCount);
    const setup = generatedCount === 0 ? setupMinutes : 0;
    const minutes = Math.ceil(setup + (callsLeft * secondsPerCall) / 60);
    if (minutes === 0) return "Almost done";
    if (minutes < 60) return `~${minutes} min left`;

    const hours = Math.floor(minutes / 60);
    const extraMinutes = minutes % 60;
    if (extraMinutes === 0) return `~${hours} hr left`;
    return `~${hours} hr ${extraMinutes} min left`;
}
